import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import {fulfilledNotify, rejectNotify, resetNotify} from "../../helpers/notifyStateHandler";

interface MainState {
    inventory_balances: any
    loading: boolean
    count: number
    refetch: boolean;
    rolesWidgets: any[];
    notify: {
        showNotification: boolean
        textNotification: string
        typeNotification: string
    }
}

const initialState: MainState = {
    inventory_balances: [],
    loading: false,
    count: 0,
    refetch: false,
    rolesWidgets: [],
    notify: {
        showNotification: false,
        textNotification: '',
        typeNotification: 'warn',
    },
}

export const fetch = createAsyncThunk('inventory_balances/fetch', async (data: any) => {
    const { id, query } = data
    const result = await axios.get(
        `inventory_balances${
            query || (id ? `/${id}` : '')
        }`
    )
    return id ? result.data : {rows: result.data.rows, count: result.data.count};
})

export const deleteItemsByIds = createAsyncThunk(
  'inventory_balances/deleteByIds',
  async (data: any, { rejectWithValue }) => {
    try {
      await axios.post('inventory_balances/deleteByIds', { data });
    } catch (error) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteItem = createAsyncThunk('inventory_balances/deleteInventory_balances', async (id: string, { rejectWithValue }) => {
    try {
        await axios.delete(`inventory_balances/${id}`)
    } catch (error) {
        if (!error.response) {
            throw error;
        }

        return rejectWithValue(error.response.data);
    }
})

export const create = createAsyncThunk('inventory_balances/createInventory_balances', async (data: any, { rejectWithValue }) => {
    try {
        const result = await axios.post(
          'inventory_balances',
          { data }
        )
        return result.data
    } catch (error) {
        if (!error.response) {
            throw error;
        }

        return rejectWithValue(error.response.data);
    }
})

export const uploadCsv = createAsyncThunk(
  'inventory_balances/uploadCsv',
  async (file: File, { rejectWithValue }) => {
      try {
          const data = new FormData();
          data.append('file', file);
          data.append('filename', file.name);

          const result = await axios.post('inventory_balances/bulk-import', data, {
              headers: {
                  'Content-Type': 'multipart/form-data',
              },
          });

          return result.data;
      } catch (error) {
          if (!error.response) {
              throw error;
          }

          return rejectWithValue(error.response.data);
      }
  },
);

export const previewPacking = createAsyncThunk(
  'inventory_balances/previewPacking',
  async ({ file, sheetName }: { file: File; sheetName: string }) => {
    const data = new FormData();
    data.append('file', file);
    if (sheetName) data.append('sheetName', sheetName);
    try {
      const result = await axios.post('/inventory_balances/excel-preview', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      return result.data;
    } catch (error) {
      console.error('Excel preview request failed:', error.response?.data || error);
      throw new Error(typeof error.response?.data === 'string' ? error.response.data : error.message);
    }
  },
);

export const importPacking = createAsyncThunk(
  'inventory_balances/importPacking',
  async ({ file, sheetName }: { file: File; sheetName: string }) => {
    const data = new FormData();
    data.append('file', file);
    if (sheetName) data.append('sheetName', sheetName);
    try {
      const result = await axios.post('/inventory_balances/excel-import', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      return result.data;
    } catch (error) {
      console.error('Excel import request failed:', error.response?.data || error);
      throw new Error(typeof error.response?.data === 'string' ? error.response.data : error.message);
    }
  },
);

export const update = createAsyncThunk('inventory_balances/updateInventory_balances', async (payload: any, { rejectWithValue }) => {
    try {
        const result = await axios.put(
          `inventory_balances/${payload.id}`,
          { id: payload.id, data: payload.data }
        )
        return result.data
    } catch (error) {
        if (!error.response) {
            throw error;
        }

        return rejectWithValue(error.response.data);
    }
})


export const inventory_balancesSlice = createSlice({
    name: 'inventory_balances',
    initialState,
    reducers: {
        setRefetch: (state, action: PayloadAction<boolean>) => {
            state.refetch = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetch.pending, (state) => {
            state.loading = true
            resetNotify(state);
        })
        builder.addCase(fetch.rejected, (state, action) => {
            state.loading = false
            rejectNotify(state, action);
        })

        builder.addCase(fetch.fulfilled, (state, action) => {
            if (action.payload.rows && action.payload.count >= 0) {
                state.inventory_balances = action.payload.rows;
                state.count = action.payload.count;
            } else {
                state.inventory_balances = action.payload;
            }
            state.loading = false
        })

        builder.addCase(deleteItemsByIds.pending, (state) => {
            state.loading = true;
            resetNotify(state);
        });

        builder.addCase(deleteItemsByIds.fulfilled, (state) => {
            state.loading = false;
            fulfilledNotify(state, 'Inventory_balances has been deleted');
        });

        builder.addCase(deleteItemsByIds.rejected, (state, action) => {
            state.loading = false;
            rejectNotify(state, action);
        });

        builder.addCase(deleteItem.pending, (state) => {
            state.loading = true
            resetNotify(state);
        })

        builder.addCase(deleteItem.fulfilled, (state) => {
            state.loading = false
            fulfilledNotify(state, `${'Inventory_balances'.slice(0, -1)} has been deleted`);
        })

        builder.addCase(deleteItem.rejected, (state, action) => {
            state.loading = false
            rejectNotify(state, action);
        })

        builder.addCase(create.pending, (state) => {
            state.loading = true
            resetNotify(state);
        })
        builder.addCase(create.rejected, (state, action) => {
            state.loading = false
            rejectNotify(state, action);
        })

        builder.addCase(create.fulfilled, (state) => {
            state.loading = false
            fulfilledNotify(state, `${'Inventory_balances'.slice(0, -1)} has been created`);
        })

        builder.addCase(update.pending, (state) => {
            state.loading = true
            resetNotify(state);
        })
        builder.addCase(update.fulfilled, (state) => {
            state.loading = false
            fulfilledNotify(state, `${'Inventory_balances'.slice(0, -1)} has been updated`);
        })
        builder.addCase(update.rejected, (state, action) => {
            state.loading = false
            rejectNotify(state, action);
        })

        builder.addCase(uploadCsv.pending, (state) => {
            state.loading = true;
            resetNotify(state);
        })
        builder.addCase(uploadCsv.fulfilled, (state) => {
            state.loading = false;
            fulfilledNotify(state, 'Inventory_balances has been uploaded');
        })
        builder.addCase(uploadCsv.rejected, (state, action) => {
            state.loading = false;
            rejectNotify(state, action);
        })

        
    },
})

// Action creators are generated for each case reducer function
 export const { setRefetch } = inventory_balancesSlice.actions

export default inventory_balancesSlice.reducer
